/**
 * PageTabs — gestion multi-pages du builder de rapports Orion (Phase 2a).
 *
 * Porté depuis Metrix (`frontend/src/features/reports/PageTabs.tsx` +
 * `PageKebabMenu.tsx`, inliné ici). Appelle directement les endpoints
 * REST Orion (`/api/reporting/.../pages*`) plutôt qu'un store ; après
 * chaque mutation réussie, `onPagesChanged()` est appelé pour que le
 * parent recharge le rapport (source de vérité = backend).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, MoreHorizontal } from 'lucide-react';

export interface Page {
  id: number;
  name: string | null;
  position: number;
}

export interface PageTabsProps {
  clientId: number;
  reportId: number;
  pages: Page[];
  activePageId: number | null;
  /** Viewer : masque ajout/renommer/supprimer/réordonner. */
  readOnly?: boolean;
  onSelectPage: (pageId: number) => void;
  /** Appelé après chaque mutation réussie pour que le parent recharge le rapport.
   *  Peut être asynchrone : `handleAddPage` ATTEND sa résolution avant de sélectionner
   *  la page neuve, pour qu'aucun refetch ne soit en vol pendant l'édition (data-loss). */
  onPagesChanged: () => void | Promise<void>;
}

const BASE = (clientId: number, reportId: number) =>
  `/api/reporting/clients/${clientId}/reports/${reportId}/pages`;

async function parseResponse(res: Response) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.error) {
    throw new Error(body?.error || `Erreur ${res.status}`);
  }
  return body;
}

export default function PageTabs({
  clientId,
  reportId,
  pages,
  activePageId,
  readOnly = false,
  onSelectPage,
  onPagesChanged,
}: PageTabsProps) {
  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const [busy, setBusy] = useState(false);
  // Le menu kebab est rendu dans un portail (position fixed) : la barre d'onglets a
  // `overflow-x-auto` qui, par la règle CSS, clippe aussi l'axe vertical → un dropdown
  // en `absolute` passait sous le conteneur du dessous. `menuAnchor` = position du bouton.
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const portalMenuRef = useRef<HTMLDivElement>(null);

  const sorted = [...pages].sort((a, b) => a.position - b.position);

  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      const t = e.target as Node;
      const inButton = menuRef.current?.contains(t);
      const inMenu = portalMenuRef.current?.contains(t);
      if (!inButton && !inMenu) setMenuOpenId(null);
    }
    function closeOnScroll() { setMenuOpenId(null); }
    if (menuOpenId !== null) {
      document.addEventListener('mousedown', clickOutside);
      window.addEventListener('scroll', closeOnScroll, true);
    }
    return () => {
      document.removeEventListener('mousedown', clickOutside);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [menuOpenId]);

  async function handleAddPage() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(BASE(clientId, reportId), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await parseResponse(res);
      // ATTENDRE le refetch parent AVANT de sélectionner la page neuve : sinon un
      // `fetchReport` reste en vol pendant que l'utilisateur édite la page 2, résout
      // avec l'état serveur (layout vide capturé à la création) et écrase le layout
      // en mémoire → le drop suivant repart de vide et écrase le widget déjà sauvé.
      await onPagesChanged();
      if (body?.data?.id) onSelectPage(body.data.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erreur lors de la création de la page');
    } finally {
      setBusy(false);
    }
  }

  async function handleRename(pageId: number, name: string) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${BASE(clientId, reportId)}/${pageId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      await parseResponse(res);
      onPagesChanged();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erreur lors du renommage');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(pageId: number) {
    if (busy) return;
    if (sorted.length <= 1) return;
    if (!window.confirm('Supprimer cette page ?')) return;
    setBusy(true);
    try {
      const res = await fetch(`${BASE(clientId, reportId)}/${pageId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      await parseResponse(res);
      if (activePageId === pageId) {
        const remaining = sorted.filter((p) => p.id !== pageId);
        if (remaining[0]) onSelectPage(remaining[0].id);
      }
      onPagesChanged();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setBusy(false);
    }
  }

  async function handleReorder(fromId: number, toId: number) {
    if (busy || fromId === toId) return;
    const ids = sorted.map((p) => p.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    setBusy(true);
    try {
      const res = await fetch(`${BASE(clientId, reportId)}/reorder`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: next }),
      });
      await parseResponse(res);
      onPagesChanged();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erreur lors du réordonnancement');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sticky top-0 z-10 flex items-center gap-1.5 overflow-x-auto bg-white px-2 py-2.5 shadow-[0_1px_0_rgba(27,37,89,0.04)] sm:px-4">
      {sorted.map((p) => {
        const active = p.id === activePageId;
        const dragging = dragId === p.id;
        const over = overId === p.id;
        const label = p.name || `Page ${p.position + 1}`;
        const isRenaming = renamingId === p.id;

        return (
          <div
            key={p.id}
            draggable={!readOnly && !isRenaming}
            onDragStart={(e) => {
              if (readOnly) return;
              setDragId(p.id);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', String(p.id));
            }}
            onDragOver={(e) => {
              if (readOnly || !dragId || dragId === p.id) return;
              e.preventDefault();
              setOverId(p.id);
            }}
            onDragLeave={() => setOverId((o) => (o === p.id ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId && dragId !== p.id) handleReorder(dragId, p.id);
              setDragId(null);
              setOverId(null);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            onClick={() => {
              if (!isRenaming) onSelectPage(p.id);
            }}
            onDoubleClick={() => {
              if (readOnly) return;
              setDraftName(label);
              setRenamingId(p.id);
            }}
            className={[
              'group flex shrink-0 select-none items-center gap-1 rounded-lg px-3 py-1.5 text-sm transition-all',
              readOnly ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
              active
                ? 'bg-[#6366F1]/10 font-medium text-[#6366F1]'
                : 'text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#1B2559]',
              dragging ? 'opacity-50' : '',
              over ? 'ring-2 ring-[#6366F1]/40' : '',
            ].join(' ')}
          >
            {isRenaming ? (
              <input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={() => {
                  const trimmed = draftName.trim();
                  if (trimmed && trimmed !== label) handleRename(p.id, trimmed);
                  setRenamingId(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = draftName.trim();
                    if (trimmed && trimmed !== label) handleRename(p.id, trimmed);
                    setRenamingId(null);
                  }
                  if (e.key === 'Escape') setRenamingId(null);
                }}
                className="w-32 rounded border border-[#E2E4ED] px-1 py-0.5 text-sm text-[#1B2559]"
              />
            ) : (
              <span>{label}</span>
            )}

            {!readOnly && !isRenaming && (
              <div className="relative inline-block">
                <button
                  ref={menuOpenId === p.id ? menuRef : undefined}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (menuOpenId === p.id) { setMenuOpenId(null); return; }
                    const r = e.currentTarget.getBoundingClientRect();
                    setMenuAnchor({ top: r.bottom + 4, left: r.left });
                    setMenuOpenId(p.id);
                  }}
                  className="flex items-center rounded px-0.5 text-[#8B8FA7] opacity-0 transition-opacity hover:text-[#1B2559] group-hover:opacity-100"
                  aria-label="Actions de la page"
                >
                  <MoreHorizontal size={15} strokeWidth={1.75} />
                </button>
                {menuOpenId === p.id && menuAnchor && createPortal(
                  <div
                    ref={portalMenuRef}
                    className="fixed z-50 w-40 rounded-md border border-[#E2E4ED] bg-white py-1 shadow-lg"
                    style={{ top: menuAnchor.top, left: menuAnchor.left }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-sm text-[#1B2559] hover:bg-[#F4F5FA]"
                      onClick={() => {
                        setMenuOpenId(null);
                        setDraftName(label);
                        setRenamingId(p.id);
                      }}
                    >
                      Renommer
                    </button>
                    <button
                      type="button"
                      disabled={sorted.length <= 1}
                      className={[
                        'block w-full px-3 py-1.5 text-left text-sm',
                        sorted.length > 1
                          ? 'text-red-600 hover:bg-red-50'
                          : 'cursor-not-allowed text-[#8B8FA7]',
                      ].join(' ')}
                      onClick={() => {
                        setMenuOpenId(null);
                        handleDelete(p.id);
                      }}
                    >
                      Supprimer
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <button
          type="button"
          onClick={handleAddPage}
          disabled={busy}
          className="ml-1 flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-sm text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#1B2559] disabled:opacity-50"
        >
          <Plus size={14} /> Page
        </button>
      )}
    </div>
  );
}
